import * as React from 'react';

import { Slider, TextField } from '@mui/material';

export interface RangeFieldsProps {
  /** Names the range in accessible labels, e.g. "caramel minimum value". */
  label: string;
  min: number;
  max: number;
  onChange: (range: { min: number; max: number }) => void;
  bounds: [number, number];
  step?: number;
  unit?: string;
  error?: boolean;
  disabled?: boolean;
  sliderClassName?: string;
  numberClassName?: string;
  /** Reference points shown on the slider, e.g. the current value. */
  marks?: { value: number; label?: string }[];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** A two-thumb slider with matching Min and Max number fields, rendered as siblings for grid layouts. */
const RangeFields: React.FC<RangeFieldsProps> = ({
  label,
  min,
  max,
  onChange,
  bounds: [lo, hi],
  step = 1,
  unit,
  error,
  disabled,
  sliderClassName,
  numberClassName,
  marks,
}) => {
  const numberField = (which: 'min' | 'max') => (
    <TextField
      size="small"
      type="number"
      label={which === 'min' ? 'Min' : 'Max'}
      value={Number.isFinite(which === 'min' ? min : max) ? (which === 'min' ? min : max) : ''}
      onChange={(e) => {
        const value = e.target.value === '' ? NaN : clamp(Number(e.target.value), lo, hi);
        onChange(which === 'min' ? { min: value, max } : { min, max: value });
      }}
      // Spinner steps stop at the other end of the range; typed values are left to validation.
      inputProps={{
        min: which === 'max' && Number.isFinite(min) ? min : lo,
        max: which === 'min' && Number.isFinite(max) ? max : hi,
        step,
        'aria-label': `${label} ${which === 'min' ? 'minimum' : 'maximum'} value`,
      }}
      InputProps={{ endAdornment: unit }}
      className={numberClassName}
      error={error}
      disabled={disabled}
    />
  );

  return (
    <>
      <Slider
        className={sliderClassName}
        value={[min, max]}
        min={lo}
        max={hi}
        step={step}
        disabled={disabled}
        disableSwap
        marks={marks}
        onChange={(_, value) => {
          const [nextMin, nextMax] = value as number[];
          onChange({ min: nextMin, max: nextMax });
        }}
        getAriaLabel={(thumb) => `${label} ${thumb === 0 ? 'minimum' : 'maximum'}`}
        valueLabelDisplay="auto"
      />
      {numberField('min')}
      {numberField('max')}
    </>
  );
};

export default RangeFields;
